/**
 * ProjectCard Component
 * AIDLC Administrative Backend
 *
 * Individual card in the project list sidebar.
 * Shows project name, AIDLC state badge, sprint status, agent count, health dot.
 */

import { Badge } from '../shared/Badge';
import type { ProjectOverview } from '../../types/projects';

interface ProjectCardProps {
  project: ProjectOverview;
  isSelected: boolean;
  onClick: () => void;
}

const healthDotColors: Record<string, string> = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500',
};

const stateVariants: Record<string, 'info' | 'warning' | 'success' | 'neutral' | 'default' | 'purple'> = {
  intent: 'neutral',
  requirements: 'default',
  planning: 'info',
  estimating: 'warning',
  implementing: 'info',
  delivering: 'purple',
  complete: 'success',
};

export function ProjectCard({ project, isSelected, onClick }: ProjectCardProps) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 rounded-lg border transition-all ${
        isSelected
          ? 'border-ldaja-blue bg-blue-50 shadow-sm'
          : 'border-zinc-200 bg-white hover:bg-gray-50 hover:border-gray-300 dark:bg-zinc-900 dark:border-zinc-800'
      }`}
    >
      {/* Top row: name + health dot */}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate">
          {project.name}
        </h3>
        <span
          className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${healthDotColors[project.health] || 'bg-gray-400'}`}
          title={`Health: ${project.health}`}
        />
      </div>

      {/* AIDLC state badge */}
      <div className="mb-2">
        <Badge variant={stateVariants[project.aidlcState] || 'neutral'}>
          {project.aidlcState}
        </Badge>
      </div>

      {/* Sprint status */}
      <div className="text-[10px] text-gray-500 mb-1">
        {project.activeSprintName
          ? <span className="text-ldaja-blue font-medium">{project.activeSprintName}</span>
          : <span className="italic">No active sprint</span>
        }
      </div>

      {/* Bottom row: agent count + item count */}
      <div className="flex items-center gap-3 text-[10px] text-gray-400">
        <span className="flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {project.agentCount} agent{project.agentCount !== 1 ? 's' : ''}
        </span>
        <span className="flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          {project.backlogItemCount} item{project.backlogItemCount !== 1 ? 's' : ''}
        </span>
        {project.blockerCount > 0 && (
          <span className="flex items-center gap-1 text-red-500 font-medium">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            {project.blockerCount}
          </span>
        )}
      </div>
    </button>
  );
}
